import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, UserRound } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, todayIso } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Input, Select, TimePicker } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const THERAPY_UNITS = ['dialysis', 'chemotherapy', 'physiotherapy', 'day_care'];

export function BookSessionPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const requestedUnit = params.get('unit') ?? '';
  const unit = THERAPY_UNITS.includes(requestedUnit) ? requestedUnit : 'dialysis';
  const initialDate = params.get('date') ?? todayIso();
  const returnTo = safeReturnTo(params.get('returnTo'), `/therapy?unit=${unit}&date=${initialDate}`);
  const [form, setForm] = useState({
    patientId: params.get('patientId') ?? '',
    stationId: '',
    date: initialDate,
    time: '09:00',
    durationMinutes: 240,
    cycleNo: 1,
    protocol: '',
    chargeItemId: '',
  });
  const [error, setError] = useState<string | null>(null);

  const stations = useQuery({
    queryKey: ['stations', unit],
    queryFn: () => get<{ id: string; code: string; name: string }[]>(`/therapy/stations${qs({ unit })}`),
  });

  const charges = useQuery({
    queryKey: ['charge-items', 'procedure'],
    queryFn: async () => (await get<{ data: { id: string; code: string; name: string }[] }>('/charge-items?limit=200')).data,
  });

  const book = useMutation({
    mutationFn: () =>
      post('/therapy/sessions', {
        patientId: form.patientId,
        unit,
        stationId: form.stationId || null,
        cycleNo: form.cycleNo,
        protocol: form.protocol || undefined,
        scheduledAt: new Date(`${form.date}T${form.time}:00`).toISOString(),
        durationMinutes: form.durationMinutes,
        chargeItemId: form.chargeItemId || null,
      }),
    onSuccess: () => {
      toast.success('Session booked');
      void queryClient.invalidateQueries({ queryKey: ['therapy-sessions'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty =
    !book.isSuccess &&
    (form.patientId !== (params.get('patientId') ?? '') ||
      form.stationId !== '' ||
      form.date !== initialDate ||
      form.time !== '09:00' ||
      form.durationMinutes !== 240 ||
      form.cycleNo !== 1 ||
      form.protocol !== '' ||
      form.chargeItemId !== '');

  return (
    <FormScreen
      title={`Book a ${humanize(unit)} session`}
      description="Two patients cannot hold the same station at the same time"
      icon={CalendarClock}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!form.patientId) return;
        setError(null);
        book.mutate();
      }}
    >
      <FormSection title={tr('opd', 'patient')} icon={UserRound} columns={1}>
        <Field label="Patient" required>
          <PatientPicker value={form.patientId} onChange={(patientId) => setForm({ ...form, patientId })} />
        </Field>
      </FormSection>

      <FormSection title={tr('opd', 'details')} icon={CalendarClock}>
        <Field label="Station">
          <Select value={form.stationId} onChange={(e) => setForm({ ...form, stationId: e.target.value })}>
            <option value="">Assign later</option>
            {stations.data?.map((station) => (
              <option key={station.id} value={station.id}>
                {station.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Cycle / session number">
          <Input type="number" min={1} value={form.cycleNo} onChange={(e) => setForm({ ...form, cycleNo: Number(e.target.value) })} />
        </Field>
        <Field label="Date" required>
          <DatePicker value={form.date} onChange={(value) => setForm({ ...form, date: value })} />
        </Field>
        <Field label="Start" required>
          <TimePicker value={form.time} onChange={(value) => setForm({ ...form, time: value })} />
        </Field>
        <Field label="Duration (minutes)">
          <Input type="number" min={15} value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })} />
        </Field>
        <Field label="Charge">
          <Select value={form.chargeItemId} onChange={(e) => setForm({ ...form, chargeItemId: e.target.value })}>
            <option value="">Not charged</option>
            {charges.data?.map((charge) => (
              <option key={charge.id} value={charge.id}>
                {charge.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Protocol" className="sm:col-span-2">
          <Input value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })} placeholder="Bicarbonate, 4 hours" />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Book" loading={book.isPending} incomplete={!form.patientId} cancelTo={returnTo} />
    </FormScreen>
  );
}
